import ThemeToggle from "../components/ThemeToggle";

interface LoginProps {
  error: string | null;
  theme: { theme: "light" | "dark"; toggle: () => void };
}

const ERROR_COPY: Record<string, string> = {
  not_allowed:
    "That Google account isn't on the allowlist. Try signing in with an authorized account.",
  state_mismatch:
    "The sign-in session expired before it could be completed. Please try again.",
  missing_params: "Sign-in didn't return all required parameters.",
  token_exchange: "Google rejected the sign-in token exchange. Try again.",
  userinfo: "We couldn't fetch your Google profile. Try again.",
  access_denied: "You declined the requested permissions.",
  strava_unavailable:
    "Strava sign-in is not configured on this deployment yet.",
  strava_scope:
    "Strava did not grant activity read access, so it cannot drive quest progress.",
  strava_token_exchange: "Strava rejected the sign-in token exchange. Try again.",
  account_merge_required:
    "That Strava account is already linked to another Fitness RPG profile.",
};

export default function Login({ error, theme }: LoginProps) {
  const message = error ? ERROR_COPY[error] ?? `Sign-in error: ${error}` : null;

  return (
    <div className="relative flex min-h-dvh flex-col">
      <header className="flex items-center justify-between px-6 py-5">
        <Brand />
        <ThemeToggle theme={theme} />
      </header>

      <main className="flex flex-1 items-center justify-center px-6 pb-16">
        <div className="card w-full max-w-md animate-slide-up p-8">
          <div className="flex items-center gap-3">
            <Logo className="h-9 w-9" />
            <div>
              <h1 className="text-xl font-semibold tracking-tight">
                Fitness RPG
              </h1>
              <p className="text-sm text-zinc-500">
                Track your fitness quest progress.
              </p>
            </div>
          </div>

          <p className="mt-6 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
            Sign in with the fitness account you want to use. Google Health
            powers the Fitbit-style dashboard; Strava shows activity distance
            records and can drive quest progress from runs, walks, and hikes.
          </p>

          {message && (
            <div
              role="alert"
              className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/60 dark:text-red-200"
            >
              {message}
            </div>
          )}

          <a href="/api/auth/start" className="btn-primary mt-6 w-full">
            <GoogleIcon className="h-4 w-4" />
            Continue with Google
          </a>

          <a
            href="/api/auth/strava/start"
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#fc4c02] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#e34402]"
          >
            <StravaIcon className="h-4 w-4" />
            Continue with Strava
          </a>

          <p className="mt-6 text-xs leading-relaxed text-zinc-500">
            Tokens are encrypted and stored only on Cloudflare. Strava supplies
            activity distance and moving time, not Fitbit Active Zone Minutes,
            sleep, or all-day steps.
          </p>
        </div>
      </main>

      <footer className="px-6 py-6 text-center text-xs text-zinc-500">
        Private household app ·{" "}
        <a className="underline hover:text-zinc-700 dark:hover:text-zinc-300" href="/privacy">
          Privacy
        </a>{" "}
        ·{" "}
        <a className="underline hover:text-zinc-700 dark:hover:text-zinc-300" href="/terms">
          Terms
        </a>
      </footer>
    </div>
  );
}

function StravaIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="currentColor" d="M14.4 16.2 12 11.4l-2.4 4.8H6L12 4l6 12.2h-3.6Z" />
      <path fill="currentColor" d="m16.8 20-1.8-3.6h3.6L16.8 20ZM7.2 20l-1.8-3.6H9L7.2 20Z" />
    </svg>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2">
      <Logo className="h-6 w-6" />
      <span className="text-sm font-semibold tracking-tight">Fitness RPG</span>
    </div>
  );
}

function Logo({ className }: { className?: string }) {
  return (
    <img
      src="/icons/strava-app-icon-124.png"
      alt=""
      className={className}
      aria-hidden="true"
    />
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#FFC107"
        d="M21.8 10.2H12v3.9h5.6c-.8 2.4-3.1 4.1-5.6 4.1a6.1 6.1 0 1 1 0-12.2c1.6 0 3 .6 4.1 1.6l2.8-2.8A10 10 0 1 0 22 12c0-.6 0-1.2-.2-1.8Z"
      />
      <path
        fill="#FF3D00"
        d="M3.2 7.3 6.4 9.8a6.1 6.1 0 0 1 5.6-3.8c1.6 0 3 .6 4.1 1.6l2.8-2.8A10 10 0 0 0 3.2 7.3Z"
      />
      <path
        fill="#4CAF50"
        d="M12 22a10 10 0 0 0 6.7-2.6l-3.1-2.6c-1 .7-2.2 1.1-3.6 1.1-2.5 0-4.7-1.6-5.5-3.9l-3.2 2.5A10 10 0 0 0 12 22Z"
      />
      <path
        fill="#1976D2"
        d="M21.8 10.2H12v3.9h5.6c-.4 1.1-1 2-2 2.7l3.1 2.6c1.5-1.4 2.5-3.5 2.5-6 0-.6 0-1.2-.2-1.8Z"
      />
    </svg>
  );
}
